import { LINER_CATALOG, linerPatternSvg } from '../components/suitDesignerCatalog';

const API = process.env.REACT_APP_BACKEND_URL;
const images = new Map();
export const linerImageUrl = u => u?.startsWith('/') ? `${API}${u}` : u;

export function loadLinerImage(url) {
  url = linerImageUrl(url);
  if (!images.has(url)) {
    const pending = (async () => {
      let objectUrl;
      try {
        let src = url;
        if (!url.startsWith('data:')) {
          const response = await fetch(url, { credentials: 'include' });
          if (!response.ok) throw new Error('Preview image unavailable');
          objectUrl = URL.createObjectURL(await response.blob());
          src = objectUrl;
        }
        const image = new Image();
        image.src = src;
        await image.decode();
        return image;
      } finally {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      }
    })().catch(error => { images.delete(url); throw error; });
    images.set(url, pending);
    if (images.size > 100) images.delete(images.keys().next().value);
  }
  return images.get(url);
}

function pixels(image, width = image.naturalWidth, height = image.naturalHeight) {
  const canvas = document.createElement('canvas');
  canvas.width = width; canvas.height = height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, width, height);
  ctx.drawImage(image, 0, 0, width, height);
  return ctx.getImageData(0, 0, width, height);
}

export async function loadClientLinerLayers(preview) {
  const [photo, mask] = await Promise.all([
    loadLinerImage(preview.base_url), loadLinerImage(preview.mask_url),
  ]);
  const base = pixels(photo), alpha = pixels(mask);
  if (base.width !== alpha.width || base.height !== alpha.height) throw new Error('Preview layers do not align');
  const indices = [];
  let left = base.width, right = 0, top = base.height, bottom = 0;
  for (let i = 0; i < alpha.data.length; i += 4) {
    if (alpha.data[i] < 128) continue;
    indices.push(i);
    const x = (i / 4) % base.width, y = Math.floor(i / 4 / base.width);
    left = Math.min(left, x); right = Math.max(right, x);
    top = Math.min(top, y); bottom = Math.max(bottom, y);
  }
  if (!indices.length) throw new Error('No lining panels found');
  const lights = new Float32Array(indices.length), sheens = new Float32Array(indices.length);
  for (let n = 0; n < indices.length; n++) {
    const light = base.data[indices[n]] / 190;
    lights[n] = Math.pow(light, 1.1);
    sheens[n] = Math.max(0, light - 1) * 48;
  }
  return { base, indices, lights, sheens, left, right, top, bottom };
}

export function preloadLinerSwatches(library = []) {
  LINER_CATALOG.filter(l => !['solid', 'stripe'].includes(l.kind)).forEach(l => {
    loadLinerImage(linerPatternSvg(l.kind, l.color)).catch(() => {});
  });
  library.forEach(l => { if (l.image_url) loadLinerImage(l.image_url).catch(() => {}); });
}

const rgb = hex => [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16));
const wrap = x => ((x % 1) + 1) % 1;

export async function composeClientLiner(layers, design) {
  const { base, indices, lights, sheens, left, right, top, bottom } = layers;
  const liner = LINER_CATALOG.find(l => l.id === design.liner_id) || LINER_CATALOG[0];
  const custom = design.liner_custom_url;
  let texture = null;
  if (custom || !['solid', 'stripe'].includes(liner.kind)) {
    const image = await loadLinerImage(custom || linerPatternSvg(liner.kind, liner.color));
    // Bound texture working-set; keep the original aspect and full motif.
    const scale = Math.min(1, 1200 / Math.max(image.naturalWidth, image.naturalHeight));
    texture = pixels(image, Math.max(1, Math.round(image.naturalWidth * scale)), Math.max(1, Math.round(image.naturalHeight * scale)));
  }
  const output = new ImageData(new Uint8ClampedArray(base.data), base.width, base.height);
  const solid = rgb(liner.color);
  // Solid silk needs no texture coordinates or per-pixel exponentiation.
  // Keep the common selection path to one tight typed-array write loop.
  if (!texture && liner.kind === 'solid') {
    for (let n = 0; n < indices.length; n++) {
      const i = indices[n], light = lights[n], sheen = sheens[n];
      output.data[i] = solid[0] * light + sheen;
      output.data[i + 1] = solid[1] * light + sheen;
      output.data[i + 2] = solid[2] * light + sheen;
    }
    return output;
  }
  const spanW = Math.max(1, right - left), spanH = Math.max(1, bottom - top);
  for (let n = 0; n < indices.length; n++) {
    const i = indices[n];
    const x = (i / 4) % base.width, y = Math.floor(i / 4 / base.width);
    // A small displacement derived from the actual photographed fold relief,
    // not an animated/synthetic wrinkle. Outside the mask is never repainted.
    const dx = (base.data[Math.min(base.data.length - 4, i + 4)] - base.data[Math.max(0, i - 4)]) / 255;
    let u = (x - left) / spanW + dx * .016, v = (y - top) / spanH;
    let colour = solid;
    if (texture) {
      const mode = custom ? design.panel_mode : 'repeat';
      if (mode === 'repeat') {
        const tile = base.width * (custom ? .18 : liner.kind === 'hound' ? .035 : .10);
        u = wrap((x - left) / tile + dx * .08);
        v = wrap((y - top) / (tile * texture.height / texture.width));
      } else if (mode === 'triptych') {
        u = wrap(u * 3);
      }
      const tx = Math.max(0, Math.min(texture.width - 1, u * (texture.width - 1)));
      const ty = Math.max(0, Math.min(texture.height - 1, v * (texture.height - 1)));
      const x0 = Math.floor(tx), y0 = Math.floor(ty), fx = tx - x0, fy = ty - y0;
      const at = (xx, yy, c) => texture.data[(yy * texture.width + xx) * 4 + c];
      const x1 = Math.min(texture.width - 1, x0 + 1), y1 = Math.min(texture.height - 1, y0 + 1);
      colour = [0, 1, 2].map(c => (at(x0, y0, c) * (1 - fx) + at(x1, y0, c) * fx) * (1 - fy)
        + (at(x0, y1, c) * (1 - fx) + at(x1, y1, c) * fx) * fy);
    } else if (liner.kind === 'stripe' && (x + y * .5) % 20 < 3) {
      colour = solid.map(c => Math.min(255, c + 24));
    }
    for (let c = 0; c < 3; c++) output.data[i + c] = colour[c] * lights[n] + sheens[n];
  }
  return output;
}